/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { DataOptions } from "vuetify/types";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { FILTER_NAMES } from "@/static/filterNames";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { AddCashReportRequest } from "@/models/api/finances/AddCashReportRequest";
import { GetCashReportItem } from "@/models/api/finances/GetCashReportsDataTableResponse";
import {
  getCashReportsDataTable,
  postAddCashReport,
  postRemoveCashReport,
  postReprintCashReport,
  showCashReport,
} from "@/requests/finances/requestsCashReports";

export interface FinancesCashReportsStateInterface {
  dataTableData: Array<GetCashReportItem>;
  dataTableOptions: DataOptions;
  countServerSide: number;
  filters: null | Array<CriterionItemRequest>;
  selectedCashboxId: null | number;
}

export const FinancesCashReportsState: FinancesCashReportsStateInterface = {
  dataTableData: [],
  dataTableOptions: new DefaultDataOptions(),
  countServerSide: -1,
  filters: null,
  selectedCashboxId: null,
};

const FinancesCashReportsModule = defineModule({
  state: FinancesCashReportsState as FinancesCashReportsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setDataTableData(state, param: Array<GetCashReportItem>) {
      state.dataTableData = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = JSON.parse(JSON.stringify(param));
    },
    setCountServerSide(state, param: number) {
      state.countServerSide = param;
    },
    setFilters(state, param: Array<CriterionItemRequest>) {
      state.filters = param;
    },
    setSelectedCashboxId(state, param: number | null) {
      state.selectedCashboxId = param;
    },
  },
  actions: {
    async setDefaultCashbox(context) {
      const { rootState, commit } = _moduleActionContext(context);
      const userCashbox = rootState.cashboxes.userCashboxes;
      if (userCashbox !== null && userCashbox.length > 0) {
        commit.setSelectedCashboxId(userCashbox[0].id);
      }
    },
    async getCashReportsTable(context) {
      const { dispatch, commit, state } = _moduleActionContext(context);
      if (state.selectedCashboxId === null) {
        await dispatch.setDefaultCashbox();
      }
      const response = await getCashReportsDataTable(
        {
          count: state.dataTableOptions.itemsPerPage,
          page: state.dataTableOptions.page - 1,
          criterions: state.filters,
          filterName: FILTER_NAMES.CashReportsStandardFilter,
        },
        state.selectedCashboxId as number
      );
      commit.setDataTableData(response.items);
      commit.setCountServerSide(response.totalCount);
    },
    async addCashReport(context, params: { fromDate: string; toDate: string }) {
      const { dispatch, rootDispatch, state } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      const request = new AddCashReportRequest();
      request.fromDate = params.fromDate;
      request.toDate = params.toDate;
      request.cashboxId = state.selectedCashboxId as number;
      const documentId = await postAddCashReport(request);
      // noinspection ES6MissingAwait
      rootDispatch.popup.addSavedOkSnackbar();
      // noinspection ES6MissingAwait
      dispatch.getCashReportsTable();
      // noinspection ES6MissingAwait
      dispatch.showDocumentRequestForPayment(documentId);
    },
    async reprintReport(context, cashReportId: number) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      await postReprintCashReport(cashReportId);
      // noinspection ES6MissingAwait
      rootDispatch.popup.addSavedOkSnackbar();
      // noinspection ES6MissingAwait
      dispatch.getCashReportsTable();
    },
    async showDocumentRequestForPayment(context, cashReportId: number) {
      await showCashReport(cashReportId);
    },
    async removeCashReport(context, cashReportId: number) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (await rootDispatch.popup.addConfirmRemoveElement()) {
        await postRemoveCashReport(cashReportId);
        // noinspection ES6MissingAwait
        dispatch.getCashReportsTable();
        rootDispatch.popup.addSavedOkSnackbar();
      }
    },
  },
});

export default FinancesCashReportsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, FinancesCashReportsModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, FinancesCashReportsModule);
